<template>
  <form id="form-add-medicine" class="position-relative">
    <loader :loading="loadingForm" block />
    <div class="vuedl-layout__closeBtn" @click="close(true)">
      ×
    </div>
    <v-card>
      <v-card-title class="headline grey lighten-2 mb-4" primary-title>Выписать рецепт</v-card-title>

      <v-card-text class="py-1">
        <v-autocomplete
          label="Поиск лекарства по нормализованному наименованию"
          placeholder="Нормализованное наименование лекарства"
          hide-no-data
          hide-selected
          prepend-icon="mdi-plus"
          item-text="name"
          item-value="id"
          return-object
          :value="form.mnn_id"
          :items="drugsList.mnn"
          :loading="loadingDrugs === 'mnn'"
          :error-messages="nameErrors"
          :search-input.sync="searchDrugs.mnn"
          @input="$v.form.mnn_id.$touch()"
          @blur="$v.form.mnn_id.$touch()"
          @change="selectDrug($event, 'mnn')"
          @click:prepend="addNewMedicineName"
          @update:search-input="searchDrugsRequest($event, 'mnn')"
        >
        </v-autocomplete>
      </v-card-text>
      <v-card-text class="py-1">
        <v-autocomplete
          label="Поиск лекарства по торговому наименованию"
          placeholder="Торговое наименование лекарства"
          hide-no-data
          hide-selected
          prepend-icon="mdi-plus"
          item-text="name"
          item-value="id"
          return-object
          :value="form.drug_id"
          :items="drugsList.trade"
          :loading="loadingDrugs === 'trade'"
          :error-messages="nameErrors"
          :search-input.sync="searchDrugs.trade"
          @change="selectDrug($event, 'trade')"
          @click:prepend="addNewMedicineName"
          @update:search-input="searchDrugsRequest($event, 'trade')"
        >
        </v-autocomplete>
      </v-card-text>

      <v-card-text class="py-1">
        <v-row>
          <v-col class="py-0">
            <v-text-field label="Дозировка" v-model.number="form.dosage_amount" />
          </v-col>

          <v-col class="py-0">
            <v-select
              label="Ед. измерения"
              item-text="text"
              item-value="id"
              v-model="form.measure_unit_id"
              :loading="loadingFieldsData"
              :items="measures"
              :error-messages="measureErrors"
              @change="$v.form.measure_unit_id.$touch()"
              @blur="$v.form.measure_unit_id.$touch()"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="py-1">
        <v-subheader class="font-weight-bold px-0">Курс</v-subheader>

        <v-row>
          <v-col class="py-0">
            <v-text-field
              label=""
              v-model.number="form.intake_amount"
              :error-messages="courseCountErrors"
              @input="$v.form.intake_amount.$touch()"
              @blur="$v.form.intake_amount.$touch()"
            />
          </v-col>

          <v-col cols="auto" class="align-self-end text-center py-0">раз в</v-col>

          <v-col class="py-0">
            <v-text-field
              label=""
              v-model.number="form.period_amount"
              :error-messages="coursePeriodCountErrors"
              @input="$v.form.period_amount.$touch()"
              @blur="$v.form.period_amount.$touch()"
            />
          </v-col>

          <v-col class="py-0">
            <v-select
              label="Период"
              item-value="id"
              item-text="text"
              v-model.number="form.period_unit_id"
              :loading="loadingFieldsData"
              :items="period_amounts"
              :error-messages="coursePeriodTypeErrors"
              @change="$v.form.period_unit_id.$touch()"
              @blur="$v.form.period_unit_id.$touch()"
            />
          </v-col>
        </v-row>

        <v-row class="align-items-end">
          <v-col class="py-0">
            <v-text-field
              label="В течение"
              v-model.number="form.duration"
              :error-messages="courseDuringCountErrors"
              @input="$v.form.duration.$touch()"
              @blur="$v.form.duration.$touch()"
            />
          </v-col>

          <v-col class="py-0">
            <v-select
              label="Период"
              item-value="id"
              item-text="text"
              v-model.number="form.duration_unit_id"
              :loading="loadingFieldsData"
              :items="period_amounts"
              :error-messages="courseDuringTypeErrors"
              @change="
                changeDuringPeriodType($event);
                $v.form.duration_unit_id.$touch();
              "
              @blur="$v.form.duration_unit_id.$touch()"
            />
          </v-col>
          <v-col class="py-0">
            <date-picker
              label="Начало приёма"
              :hide-details="false"
              v-model="form.start_at"
              :error-messages="periodFromErrors"
              @input="$v.form.start_at.$touch()"
              @blur="$v.form.start_at.$touch()"
            />
          </v-col>
        </v-row>
        <v-select
          v-model="form.times"
          :items="timesList"
          :menu-props="{ maxHeight: '400' }"
          label="Время приёма"
          multiple
          class="mt-12"
          hint="Оставьте пустым для автоматической генерации"
          persistent-hint
        ></v-select>
      </v-card-text>

      <v-card-text class="py-1">
        <v-subheader class="font-weight-bold px-0">От какого заболевания</v-subheader>
        <div class="d-flex">
          <v-autocomplete
            label="Диагноз"
            hide-no-data
            hide-selected
            item-text="name"
            item-value="customerDiseaseId"
            return-object
            v-model="form.customer_disease_id"
            :items="customerDiseasesList"
            :error-messages="customerDiseaseIdErrors"
            class="mr-3"
          >
          </v-autocomplete>
        </div>
      </v-card-text>

      <v-divider class="mt-4"></v-divider>

      <v-card-actions>
        <v-btn id="form-add-medicine-cancel" color="primary" text @click="close(true)">Отменить</v-btn>
        <v-spacer></v-spacer>
        <v-btn id="form-add-medicine-submit" color="primary" text @click="submit">
          Сохранить
        </v-btn>
      </v-card-actions>
    </v-card>
  </form>
</template>

<script>
import { numeric, required } from "vuelidate/lib/validators";
import { messageHelper } from "@/services/validation-helper";
import DatePicker from "@/components/forms/DatePicker";
import SearchMKB10 from "@/components/SearchMKB10";
import Loader from "@/components/Loader";
import TimePicker from "@/components/forms/TimePicker";
import dayjs from "dayjs";

// {
//     +"mnn_id": 1, // ID лекарства
//     +"dosage_amount": 1, // дозировка, 400 мг, 1 таблетка
//     +"measure_unit_id": 79, // id единицы измерения дозировки - таблетки, граммы, литры https://medico-api.godoc.ru/api/v1/medicines/measures?lang=ru
//     +"customer_disease_id": null, //ID диагноза пациента
//     +"duration": 1, // продолжительность курса 1 неделя, 1 месяц
//     +"duration_unit_id": 3, // id единицы измерения продолжительности курса - день, неделя год https://medico-api.godoc.ru/api/v1/medicines/course-periods?lang=ru
//     +"intake_amount": 1, //количество приемов за период , 3 раза в день - значит 3
//     +"period_amount": 1, // продолжительность периода 1 раз в 1 день, 1 раз в 2 дня - значит 1 или 2
//     "+period_unit_id": 1, // id единицы измерения продолжительности периода https://medico-api.godoc.ru/api/v1/medicines/course-periods?lang=ru
//     +"start_at": "2023-06-28 14:00:00" // дата начала
//   }

// Дефолтное состояние формы
const formState = {
  mnn_id: "",
  drug_id: "",
  dosage_amount: "",
  measure_unit_id: "",
  customer_disease_id: "",
  duration: "",
  duration_unit_id: "",
  intake_amount: "",
  period_amount: "",
  period_unit_id: "",
  start_at: "",
  times: [],
};

export default {
  name: "AddMedicineReciept",

  components: { TimePicker, DatePicker, SearchMKB10, Loader },
  props: {
    date: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    diseasesTypes: [],
    loadingForm: false,
    selectedTab: 0,
    selectedPanel: 0,
    loadingFieldsData: false,

    drugFormsListLoading: false,
    drugFormsList: [],

    manufacturerListLoading: false,
    manufacturerList: [],

    searchTimeout: null,

    receptionMethods: [],
    measures: [],
    meals: [],
    period_amounts: [],

    searchDrugs: {
      common: null,
      mnn: null,
      trade: null,
    },
    loadingDrugs: false,

    drugsList: {
      mnn: [],
      trade: [],
    },

    searchDisease: null,
    loadingDiseases: false,
    diseasesList: [],

    customerDiseasesList: [],

    form: { ...formState },
    timesList: [
      "00:00",
      "00:15",
      "00:30",
      "00:45",
      "01:00",
      "01:15",
      "01:30",
      "01:45",
      "02:00",
      "02:15",
      "02:30",
      "02:45",
      "03:00",
      "03:15",
      "03:30",
      "03:45",
      "04:00",
      "04:15",
      "04:30",
      "04:45",
      "05:00",
      "05:15",
      "05:30",
      "05:45",
      "06:00",
      "06:15",
      "06:30",
      "06:45",
      "07:00",
      "07:15",
      "07:30",
      "07:45",
      "08:00",
      "08:15",
      "08:30",
      "08:45",
      "09:00",
      "09:15",
      "09:30",
      "09:45",
      "10:00",
      "10:15",
      "10:30",
      "10:45",
      "11:00",
      "11:15",
      "11:30",
      "11:45",
      "12:00",
      "12:15",
      "12:30",
      "12:45",
      "13:00",
      "13:15",
      "13:30",
      "13:45",
      "14:00",
      "14:15",
      "14:30",
      "14:45",
      "15:00",
      "15:15",
      "15:30",
      "15:45",
      "16:00",
      "16:15",
      "16:30",
      "16:45",
      "17:00",
      "17:15",
      "17:30",
      "17:45",
      "18:00",
      "18:15",
      "18:30",
      "18:45",
      "19:00",
      "19:15",
      "19:30",
      "19:45",
      "20:00",
      "20:15",
      "20:30",
      "20:45",
      "21:00",
      "21:15",
      "21:30",
      "21:45",
      "22:00",
      "22:15",
      "22:30",
      "22:45",
      "23:00",
      "23:15",
      "23:30",
      "23:45",
    ],
  }),

  validations: {
    form: {
      mnn_id: { required },
      dosage_amount: { required },
      measure_unit_id: { required },
      customer_disease_id: { required },
      duration: { required },
      duration_unit_id: { required },
      intake_amount: { required },
      period_amount: { required },
      period_unit_id: { required },
      start_at: { required },
    },
  },

  computed: {
    nameErrors() {
      return messageHelper(this.$v.form.mnn_id);
    },

    measureErrors() {
      return messageHelper(this.$v.form.measure_unit_id);
    },

    courseCountErrors() {
      return messageHelper(this.$v.form.intake_amount);
    },

    coursePeriodCountErrors() {
      return messageHelper(this.$v.form.period_amount);
    },

    coursePeriodTypeErrors() {
      return messageHelper(this.$v.form.period_unit_id);
    },

    courseDuringCountErrors() {
      return messageHelper(this.$v.form.duration);
    },

    courseDuringTypeErrors() {
      return messageHelper(this.$v.form.duration_unit_id);
    },

    periodFromErrors() {
      return messageHelper(this.$v.form.start_at);
    },

    // periodToErrors() {
    //   return messageHelper(this.$v.form.period.to);
    // },

    diseaseTypeIdErrors() {
      return messageHelper(this.$v.form.disease.type_id);
    },
    customerDiseaseIdErrors() {
      return messageHelper(this.$v.form.customer_disease_id);
    },
  },

  watch: {
    "form.start_at": function(newVal, oldVal) {
      if (newVal === oldVal) return;

      this.changeDuringPeriodType(this.form.duration_unit_id);
    },

    "form.duration": function(newVal, oldVal) {
      if (newVal === oldVal) return;

      this.changeDuringPeriodType(this.form.duration_unit_id);
    },

    // "form.intake_amount": {
    //   handler: function(newVal) {
    //     const count = +newVal;
    //     this.form.course.times = [];
    //     for (let i = 0; i < count; i++) {
    //       this.form.course.times.push({ time: "", idx: i });
    //     }
    //   },
    //   immediate: true,
    // },
  },

  mounted() {
    if (this.date) {
      this.form.start_at = this.date;
    }
    this.loadFormFieldsData();
  },

  methods: {
    // Посик леакртсва по названию
    searchDrugsRequest(value, type) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      if (value) {
        this.searchTimeout = setTimeout(() => {
          this.loadingDrugs = type;
          //   поскольку добавили название мнн в торговое название, нужно в поиск передавать только его
          const drugValue = value.split(",")[0];
          this.$http
            .get(type === "trade" ? `v1/drugs/trade-names` : `v1/drugs/mnns/`, { params: { search: drugValue } })
            .then(response => {
              this.drugsList[type] =
                type === "mnn"
                  ? response.data.data
                  : response.data.data.map(el => {
                      el.name += `, ${el.mnn.name}`;
                      return el;
                    });
              this.loadingDrugs = false;
            })
            .catch(e => {
              console.log(`e =>`, e);
              console.error("Ошибка поиска по лекарствам", e.response?.data?.message);
              this.loadingDrugs = false;
            });
        }, 300);
      }
    },
    loading(val) {
      this.loadingForm = val;
      this.$emit("loading", val);
    },

    /**
     * Сброс формы
     */
    resetForm() {
      this.$v.$reset();
      this.form = { ...formState };
    },

    /**
     * Отправка формы
     */
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$pending || this.$v.form.$error) return;
      this.loading(true);

      try {
        const localForm = Object.assign({}, this.form);
        console.log(`localForm =>`, localForm);
        localForm.customer_disease_id = this.form.customer_disease_id.id;
        const response = await this.$http.post(`v1/customers/${this.$store.state.customer.id}/drugs`, localForm);
        console.log(`response =>`, response);
        this.loading(false);

        if (response.status === 200) {
          this.$store.commit("reciepts/ADD_ITEM", response.data);
          this.$dialog.message.success("Рецепт добавлен");
          this.close();
        }
      } catch (e) {
        this.loading(false);
        this.$dialog.message.error(e.response.data.message);
        console.error("Ошибка добавления рецепта", e.response.data.message);
      }
    },

    /**
     * Закрытия модального окна
     */
    close() {
      this.resetForm();
      this.dialog = false;
      this.loadingForm = false;
      this.$emit("close");
      this.$emit("submit");
    },

    /**
     * Вызывает форму добавления нового лекарства
     */
    addNewMedicineName() {
      alert("Новое лекарство");
    },

    onSelectMKB10(item) {
      this.diseasesList = [item];
      this.selectedTab = 0;
    },

    /**
     * Обработчик выбора лекарства
     */
    selectDrug(item, type) {
      console.log(`item =>`, item, type);
      const id = type === "trade" ? item.mnn_id : item.id;
      this.form.mnn_id = id;
      this.loadManufactures(id);
      this.loadDrugForms(id);
      if (type === "trade") {
        this.drugsList.mnn.push(item.mnn);
        this.form.drug_id = item.id;
      }
      if (type === "mnn") {
        // this.drugsList.mnn.push(item.mnn);
        this.fillTradeList(id);
      }
    },

    /**
     * Изменяет период и диапазон дат "С - ДО"
     */
    changeDuringPeriodType(/* period */) {
      //   const format = "YYYY-MM-DD";
      //   switch (period) {
      //     case 1:
      //       this.form.period.to = dayjs(this.form.start_at)
      //         .add(this.form.duration, "day")
      //         .format(format);
      //       break;
      //     case 2:
      //       this.form.period.to = dayjs(this.form.start_at)
      //         .add(this.form.duration, "week")
      //         .format(format);
      //       break;
      //     case 3:
      //       this.form.period.to = dayjs(this.form.start_at)
      //         .add(this.form.duration, "month")
      //         .format(format);
      //       break;
      //     default:
      //       break;
      //   }
    },
    async fillTradeList(mnn_id) {
      try {
        const response = await this.$http.get(`v1/drugs/trade-names/?mnn_id=${mnn_id}`);

        // console.log(`response.data.data =>`, response.data.data);
        this.drugsList.trade = response.data.data.map(el => {
          el.name += `, ${el.mnn.name}`;
          return el;
        });
        console.log(`this.drugsList.trade =>`, this.drugsList.trade);
        this.$nextTick(() => {
          this.searchDrugs.trade = this.searchDrugs.mnn;
        });
      } catch (e) {
        console.error("Ошибка загрузки торговых наименований", e.response.data.message);
      }
    },
    async loadManufactures(mnn_id) {
      try {
        this.manufacturerListLoading = true;
        const response = await this.$http.get(`v1/medicines/${mnn_id}/manufacturers`);
        this.manufacturerList = response.data.data;
        this.manufacturerListLoading = false;

        if (this.manufacturerList.length === 1) {
          this.form.manufacturer = this.manufacturerList[0].id;
        }
      } catch (e) {
        console.error("Ошибка загрузки производителей", e.response.data.message);
      }
    },

    async loadDrugForms(mnn_id) {
      try {
        this.drugFormsListLoading = true;
        const response = await this.$http.get(`v1/medicines/${mnn_id}/forms`);
        this.drugFormsList = response.data.data;
        this.drugFormsListLoading = false;

        if (this.drugFormsList.length === 1) {
          this.form.form = this.drugFormsList[0].id;
        }
      } catch (e) {
        console.error("Ошибка загрузки форм выпуска лекарства", e.response.data.message);
      }
    },

    /**
     * Загружает данные для полей формы
     *
     * @return void
     */
    async loadFormFieldsData() {
      const [methods, measures, meals, coursePeriods, customerDiseases] = await Promise.all([
        this.$http.get(`v1/medicines/methods`),
        this.$http.get(`v1/medicines/measures`),
        this.$http.get(`v1/medicines/meals`),
        this.$http.get(`v1/medicines/course-periods`),
        this.$http.get(`v1/customers/${this.$store.state.customer.id}/diseases?sort_field=&sort_direction=&per_page=100`),
      ]);

      this.receptionMethods = methods.data.data;
      this.measures = measures.data.data;
      this.meals = meals.data.data;
      this.period_amounts = coursePeriods.data.data;
      this.customerDiseasesList = customerDiseases.data.data.map(item => ({
        customerDiseaseId: +item.id,
        id: item.id,
        type: item.type,
        code: item.icd10 ? item.icd10.code : "",
        name: item.title || (item.icd10 && item.icd10.translations ? item.icd10.translations.ru.name : ""),
      }));
    },
  },
};
</script>

<style scoped></style>
